






























import {Component, Vue, Watch} from 'vue-property-decorator';
import {mapGetters} from 'vuex';
import FileSystemObject from '@/components/FileSystemObject.vue';
import InputDialog, {InputDialogAction} from '@/components/InputDialog.vue';
import ResizeDialog from '@/components/ResizeDialog.vue';
import FileSystemObjectModel from '@/model/FileSystemObjectModel';
import File from '@/model/File';
import Image from '@/model/Image';
import Directory from '@/model/Directory';
import FileSystemObjectType from '@/enums/FileSystemObjectType';
import FileManager from '@/components/FileManager.vue';
import ApiConnector from '@/services/ApiConnector';
import FileSystemFactory from '@/factories/FileSystemFactory';
import DirectoryFactory from '@/factories/DirectoryFactory';
import AlertType from '@/enums/AlertType';
import {FileSystemEvents} from '@/events';

@Component({
	name: 'FileInspector',
	components: {
		InputDialog,
		ResizeDialog,
		FileSystemObject
	},
	computed: mapGetters([
		'actionHint'
	])
})
export default class FileInspector extends Vue {
	private FileSystemObjectType = FileSystemObjectType;

	private fileManager: FileManager = null;

	private actionHint: string;

	private apiConnector: ApiConnector;
	private currentDirectory: Directory = null;

	private showInputDialog: boolean = false;
	private inputDialogTitle: string = null;
	private inputDialogValue: string = null;
	private inputDialogAction: InputDialogAction = null;

	private showResizeDialog: boolean = false;
	private resizeDialogWidth: number = 0;
	private resizeDialogHeight: number = 0;
	private resizeDialogImage: Image = null;

	private renamedFileSystemObjectModel: FileSystemObjectModel;

	constructor()
	{
		super();

		this.fileManager = this.$parent as FileManager;
		this.apiConnector = new ApiConnector();
	}

	public mounted()
	{
		this.loadFileSystem();
	}

	public loadFileSystem()
	{
		this.apiConnector.loadFileSystem().then((data) => {
			this.currentDirectory = FileSystemFactory.create(data);
		});
	}

	@Watch('currentDirectory')
	public currentDirectoryChanged(directory: Directory)
	{
		this.$emit(FileSystemEvents.fileSystemLocationChanged, directory);
	}

	public openDirectory(directory: Directory)
	{
		if (directory)
		{
			this.currentDirectory = directory;
		}
	}

	public createDirectory()
	{
		this.showInputDialog = true;
		this.inputDialogAction = InputDialogAction.CreateDirectory;
		this.inputDialogTitle = 'Verzeichnis erstellen';
	}

	public downloadCurrentDirectory()
	{
		this.apiConnector.downloadDirectory(this.currentDirectory);
	}

	public downloadFileSystemObjectModel(fileSystemObjectModel: FileSystemObjectModel)
	{
		if (fileSystemObjectModel instanceof Directory)
		{
			this.apiConnector.downloadDirectory(fileSystemObjectModel);
		}

		if (fileSystemObjectModel instanceof File)
		{
			this.apiConnector.downloadFile(fileSystemObjectModel);
		}
	}

	public moveDirectoryUp()
	{
		if (this.currentDirectory.parent)
		{
			this.currentDirectory = this.currentDirectory.parent;
		}
	}

	public resizeImage(image: Image)
	{
		//check if file system object model is of type image
		if (image.mimeType.indexOf('image') >= 0)
		{
			this.apiConnector.getImageDimensions(image).then((dimensions: any) => {
				if (dimensions)
				{
					this.resizeDialogWidth = dimensions.width;
					this.resizeDialogHeight = dimensions.height;
					this.resizeDialogImage = image;
					this.showResizeDialog = true;
				}
			});
		}
	}

	public copyFileSystemObjectModel(fileSystemObjectModel: FileSystemObjectModel)
	{
		if (fileSystemObjectModel instanceof File)
		{
			this.apiConnector.copyFile(fileSystemObjectModel).then((newFile: File) => {
				if (newFile)
				{
					this.currentDirectory.files.push(newFile);
				}
			});
		}

		if (fileSystemObjectModel instanceof Directory)
		{
			this.apiConnector.copyDirectory(fileSystemObjectModel).then((newDirectory: Directory) => {
				if (newDirectory)
				{
					this.currentDirectory.children.push(newDirectory);
				}
			});
		}
	}

	public moveFileSystemObjectModel(fileSystemObjectModel: FileSystemObjectModel, to: Directory)
	{
		if (fileSystemObjectModel instanceof Directory)
		{
			this.apiConnector.moveDirectory(fileSystemObjectModel, to).then((success) => {
				if (success)
				{
					fileSystemObjectModel.parent.children.splice(fileSystemObjectModel.parent.children.findIndex((element) => {
						return element === fileSystemObjectModel;
					}), 1);

					to.children.push(fileSystemObjectModel);

					fileSystemObjectModel.parent = to;
				}
			});
		}

		if (fileSystemObjectModel instanceof File)
		{
			this.apiConnector.moveFile(fileSystemObjectModel, to).then((success) => {
				if (success)
				{
					fileSystemObjectModel.directory.files.splice(fileSystemObjectModel.directory.files.findIndex((element) => {
						return element === fileSystemObjectModel;
					}), 1);

					to.files.push(fileSystemObjectModel);

					fileSystemObjectModel.directory = to;
				}
			});
		}
	}

	public renameFileSystemObjectModel(fileSystemObjectModel: FileSystemObjectModel)
	{
		this.showInputDialog = true;
		this.renamedFileSystemObjectModel = fileSystemObjectModel;
		this.inputDialogAction = InputDialogAction.Rename;
		this.inputDialogTitle = 'Umbenennen';
		this.inputDialogValue = fileSystemObjectModel.name;
	}

	public onInputConfirmed(value: string)
	{
		if (this.inputDialogAction === InputDialogAction.Rename && this.renamedFileSystemObjectModel)
		{
			if (this.renamedFileSystemObjectModel instanceof Directory)
			{
				this.apiConnector.renameDirectory(this.renamedFileSystemObjectModel, value).then((success) => {
					this.showInputDialog = false;

					if (success)
					{
						this.renamedFileSystemObjectModel.name = value;
					}
				});
			}

			if (this.renamedFileSystemObjectModel instanceof File)
			{
				this.apiConnector.renameFile(this.renamedFileSystemObjectModel, value).then((success) => {
					this.showInputDialog = false;

					if (success)
					{
						this.renamedFileSystemObjectModel.name = value;
					}
				});
			}
		}

		if (this.inputDialogAction === InputDialogAction.CreateDirectory)
		{
			const options = {
				name: value
			};

			const newDirectory = DirectoryFactory.create(this.currentDirectory, options);

			this.apiConnector.createDirectory(newDirectory).then((success) => {
				if (success)
				{
					this.currentDirectory.children.push(newDirectory);

					this.fileManager.alert('Verzeichnis erfolgreich erstellt!', AlertType.Success);
				}
				else
				{
					this.fileManager.alert('Das Verzeichnis konnte nicht erstellt werden!', AlertType.Danger);
				}

				this.resetInputDialog();
			});
		}
	}

	public onInputCanceled()
	{
		this.resetInputDialog();
	}

	public onResizeConfirmed(newDimensions: any)
	{
		this.apiConnector.resizeImage(this.resizeDialogImage, newDimensions.width, newDimensions.height).then((success) => {
			this.resetResizeDialog();
		});
	}

	public onResizeCanceled()
	{
		this.resetResizeDialog();
	}

	public deleteFileSystemObjectModel(fileSystemObjectModel: FileSystemObjectModel)
	{
		if (fileSystemObjectModel instanceof Directory)
		{
			this.apiConnector.deleteDirectory(fileSystemObjectModel).then((response: any) => {
				if (response.success)
				{
					this.currentDirectory.children.splice(this.currentDirectory.children.findIndex((element) => {
						return element.id === fileSystemObjectModel.id;
					}), 1);
				}
				else
				{
					this.fileManager.alert(response.message ? response.message : 'Das Verzeichnis konnte nicht gelöscht werden!', AlertType.Danger);
				}
			});
		}

		if (fileSystemObjectModel instanceof File)
		{
			this.apiConnector.deleteFile(fileSystemObjectModel).then((response: any) => {
				if (response.success)
				{
					this.currentDirectory.files.splice(this.currentDirectory.files.findIndex((element) => {
						return element.id === fileSystemObjectModel.id;
					}), 1);
				}
				else
				{
					this.fileManager.alert(response.message ? response.message : 'Die Datei konnte nicht gelöscht werden!', AlertType.Danger);
				}
			});
		}
	}

	private resetInputDialog()
	{
		this.showInputDialog = false;
		this.inputDialogAction = null;
		this.renamedFileSystemObjectModel = null;
	}

	private resetResizeDialog()
	{
		this.showResizeDialog = false;
		this.resizeDialogWidth = 0;
		this.resizeDialogHeight = 0;
		this.resizeDialogImage = null;
	}
}
